import { zip } from 'rxjs/operators';
import { Component, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, Validators, UntypedFormArray } from '@angular/forms';
import { Location } from '@angular/common';

import * as _ from 'lodash';

import { ModalComponent } from '../../modal/modal.component';
import { MachineSelectorPluginService } from './machineselector-plugin.service';
import { BaseModalPageComponent } from '../../base-modal.component';
import { ConfirmComponent } from '../../modal/confirm.component';
import { MachineFamillyService } from '../../shared/services';
import { SnackBarService } from '../../shared/services/snack-bar.service';
import { StorageAccessor } from '../../shared/services/storage-accessor';

@Component({
    selector: 'machineselector-edit',
    templateUrl: './machineselector-edit.component.html',
    standalone: false
})
export class MachineSelectorEditPageComponent extends BaseModalPageComponent implements OnInit {
  inspectionId: string;
  pluginId: string;
  public plugin: any;
  public machine: any;
  public productLines: any[];
  public categories: any[];

  public modelSelectorValues: string[] = [];
  public modelSelectedValue: string;

  public productLineSelectedIndex: number = -1;
  public productLineSelectedValue: string;
  public productLineSelectorValues: any = [];

  public machineModels: any;
  public processing = false;
  @ViewChild('modelsModal', { static: true })
  modal: ModalComponent;
  @ViewChild('productLinesModal', { static: true })
  productLinesModal: ModalComponent;

  machineForm: UntypedFormGroup;
  @ViewChild('menuModal', { static: true })
  menuModal: ModalComponent;
  @ViewChild('deleteConfirm', { static: true })
  deleteConfirm: ConfirmComponent;

  constructor(
    private activatedRoute: ActivatedRoute,
    private machineFamillyService: MachineFamillyService,
    private router: Router,
    private service: MachineSelectorPluginService,
    location: Location,
    private cdRef: ChangeDetectorRef,
    protected storageAccessor: StorageAccessor,
    private snackbar: SnackBarService
  ) {
    super(location);
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) this.goToBack();

      this.inspectionId = id;

      const pluginId = params['pluginId'];
      if (pluginId == null) this.goToBack();

      this.pluginId = pluginId;

      this.initData();
    });
  }

  save(model: any) {
    const machine = _.cloneDeep(model.data) || {};
    // in case of model not found in referential, modelId is null, but we keep the name for future reference
    machine.ModelId = this.getModelId();
    machine.ModelName = this.modelSelectedValue;
    machine.Serial = machine.Serial.map((s) => s.replaceAll(' ', ''));
    this.processing = true;
    this.service.updateMachine(this.inspectionId, this.pluginId, machine).subscribe((res) => {
      this.processing = false;
      this.storageAccessor.removeItemFromSession(`machine-${this.inspectionId}`);

      for (const key in res.PartsBookExistence) {
        if (!res.PartsBookExistence[key]) {
          this.snackbar.warn('plugins.equipment-info.parts-book-not-found', 5, { serial: key });
        }
      }

      for (const key in res.PartsCatalogExistence) {
        if (!res.PartsCatalogExistence[key]) {
          this.snackbar.warn('plugins.equipment-info.parts-catalog-not-found', 5, { partsbook: key });
        }
      }
      this.goToBack();
    });
  }

  cancel() {
    this.goToBack();
  }

  ngOnInit() {
    this.initRoute();
  }

  getModelId() {
    const selectedItem = _.find(this.machineModels, (o: any) => o.Name === this.modelSelectedValue);
    if (selectedItem) {
      return selectedItem.Id;
    } else {
      return null;
    }
  }

  initData() {
    this.service.getPlugin(this.inspectionId, this.pluginId).subscribe((el) => {
      this.plugin = el;
      this.machine =
        el.inspection.data != null && el.inspection.data.machine != null ? el.inspection.data.machine : null;
      this.machine = this.machine
        ? this.machine
        : this.storageAccessor.getItemFromSession(`machine-${this.inspectionId}`);
      if (this.machine == null) {
        this.initMachine();
      }
      this.loadData();

      this.initForm();
      this.cdRef.detectChanges();
    });
  }

  loadData() {
    this.machineFamillyService
      .getCurrentScopeProductLines()
      .pipe(zip(this.machineFamillyService.getCategories(), this.service.getModels()))
      .subscribe(([lines, categories, models]) => {
        this.productLines = lines;
        this.categories = categories;
        this.machineModels = models;

        this.productLineSelectorValues = this.productLines.map((p) => p.name);
        const selectedProductLine = this.productLines.find((p) => p.id === this.machine.ProductLine);
        this.productLineSelectedValue = selectedProductLine ? selectedProductLine.name : '';
        this.productLineSelectedIndex = this.productLineSelectorValues.indexOf(this.productLineSelectedValue);

        this.modelSelectedValue = this.machine.name;
        this.updateModelSelectorValues(selectedProductLine);
      });
  }

  public updateModelSelectorValues(selectedProductLine: any) {
    if (this.productLineSelectedIndex > -1) {
      const productLineId = selectedProductLine.id;
      let categories = _.filter(this.categories, ['productLineId', productLineId]);
      categories = _.map(categories, 'id');
      const machinesComboData = _.filter(
        this.machineModels,
        (m: any) =>
          (!m.Hidden || m.Name === this.modelSelectedValue) &&
          _.find(categories, (c: any) => {
            return c === m.CategoryId;
          }) !== undefined
      );
      this.modelSelectorValues = _.map(machinesComboData, 'Name').sort();
    }
  }

  initMachine() {
    this.machine = {
      name: '',
      data: {
        YearBuilt: '',
        Serial: [],
        PartsBook: '',
        ServerPartsBook: '',
        CustomerEquipementId: '',
        MaintenanceSapContract: '',
        SwitchedOnHours: null,
        RunningHoursNoSheets: null,
        RunningHoursWithSheets: null,
        TotalProduction: null,
        CounterPrCycles: null,
        ProductLine: ''
      }
    };
  }

  public openModelsSelector(): void {
    this.openSelector(
      this.modal,
      this.modelSelectorValues.map((v) => ({ label: v, value: v })),
      this.modelSelectedValue,
      { search: true }
    );
  }

  public openProductLinesSelector(): void {
    this.openSelector(this.productLinesModal, this.productLineSelectorValues, this.productLineSelectedIndex);
  }

  public selectModelsMenu(item: any) {
    this.modelSelectedValue = item.selectedIndex;
    this.machineForm.controls['Name'].setValue(item.selectedIndex);
    this.machineForm.controls['Name'].markAsTouched();
  }

  public selectProductLinesMenu(item: any) {
    this.productLineSelectedIndex = this.productLineSelectedIndex === item.selectedIndex ? -1 : item.selectedIndex;
    this.productLineSelectedValue = this.productLineSelectorValues[this.productLineSelectedIndex];
    const data = <UntypedFormGroup>this.machineForm.controls['data'];
    const selectedProductLine = this.productLines.find((p) => p.name === this.productLineSelectedValue);
    data.controls['ProductLine'].setValue(selectedProductLine.id);
    this.updateModelSelectorValues(selectedProductLine);
    this.machineForm.controls['Name'].setValue('');
    this.modelSelectedValue = '';
  }

  public onProductLinesMenuClose() {
    const data = <UntypedFormGroup>this.machineForm.controls['data'];
    data.controls['ProductLine'].markAsTouched();
  }

  public onClose() {
    this.machineForm.controls['Name'].markAsTouched();
  }

  initForm() {
    this.machineForm = new UntypedFormGroup({
      Name: new UntypedFormControl(this.machine.name),
      data: new UntypedFormGroup({
        YearBuilt: new UntypedFormControl(this.machine.YearBuilt),
        Serial: new UntypedFormArray([]),
        PartsBook: new UntypedFormControl(this.machine.PartsBook),
        ServerPartsBook: new UntypedFormControl(this.machine.ServerPartsBook),
        CustomerEquipementId: new UntypedFormControl(this.machine.CustomerEquipementId),
        MaintenanceSapContract: new UntypedFormControl(this.machine.MaintenanceSapContract),
        ProductLine: new UntypedFormControl(this.machine.ProductLine),
        SwitchedOnHours: new UntypedFormControl(this.machine.SwitchedOnHours),
        RunningHoursNoSheets: new UntypedFormControl(this.machine.RunningHoursNoSheets),
        RunningHoursWithSheets: new UntypedFormControl(this.machine.RunningHoursWithSheets),
        TotalProduction: new UntypedFormControl(this.machine.TotalProduction),
        CounterPrCycles: new UntypedFormControl(this.machine.CounterPrCycles)
      })
    });
    if (this.machine.Serial && this.machine.Serial instanceof Array) {
      this.machine.Serial.forEach((serial) => {
        this.Serial.push(new UntypedFormControl(serial, Validators.required));
      });
    } else {
      this.Serial.push(new UntypedFormControl('', Validators.required));
    }
  }

  get formData() {
    return <UntypedFormGroup>this.machineForm.controls['data'];
  }

  get Serial() {
    return <UntypedFormArray>this.machineForm.controls['data'].get('Serial');
  }

  public addSerial() {
    this.Serial.push(new UntypedFormControl('', Validators.required));
  }

  public removeSerial(i: number) {
    this.Serial.removeAt(i);
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        this.openDeleteConfirm();
        break;
      case 10:
        this.gotoEditJsonPage();
        break;
      case 11:
        this.gotoEditPage();
        break;
      default:
    }
  }

  gotoEditJsonPage() {
    return this.router.navigate([
      this.inspectionsRoute,
      this.inspectionId,
      'points',
      -1,
      'plugins',
      this.pluginId,
      'editjson'
    ]);
  }

  gotoEditPage() {
    return this.router.navigate([
      this.inspectionsRoute,
      this.inspectionId,
      'points',
      -1,
      'plugins',
      this.pluginId,
      'edit'
    ]);
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  deletePlugin() {
    this.service.deletePlugin(this.inspectionId, null, this.pluginId).subscribe((success: boolean) => {
      if (success) {
        this.goToBack();
      }
    });
  }
}
